import React, { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { EntityContainer } from '../DynamicEntities/ui/DynamicFeatureOrchestrator';
import type { DynamicEntitiesRef } from '../DynamicEntities/ui/DynamicEntities';
import { Tabs } from '../Tabs';
import { useMasterDocumentTranslation } from './i18n';
import type { DocumentDynamicGrain } from './smart/useDocumentDynamicEntities';

export interface DocumentDynamicEntitiesProps {
  /** Grano ya resuelto por `useDocumentDynamicEntities`. */
  grain: DocumentDynamicGrain;
  /**
   * Id del registro dueño de los valores: el documento para el grano de
   * encabezado, la línea para el de línea. Ausente en creación — el formulario se
   * captura igual y los valores se persisten cuando el backend devuelve el id.
   */
  recordId?: string;
  /** Título por defecto cuando el template no declaró `label`/`labelKey`. */
  defaultTitleKey: string;
  /** El documento no es editable: el bloque se pinta pero no se puede capturar. */
  readOnly?: boolean;
  /**
   * Presentación. `card` (por defecto) envuelve el bloque en la misma tarjeta que
   * usan las secciones del encabezado; `bare` lo entrega suelto, para un anfitrión
   * que ya aporta su propio marco — hoy, el modal de línea.
   */
  variant?: 'card' | 'bare';
  className?: string;
}

/**
 * Bloque de campos adicionales (EAV) de un grano del documento.
 *
 * Es el equivalente para `MasterDocument` de `MasterCrudDynamicEntities`, y
 * mantiene sus dos decisiones importantes:
 *
 * 1. **Los `EntityContainer` van sin su botón Guardar** (`hideSubmitButton`). El
 *    documento tiene UNA acción de guardar; un segundo botón dentro del formulario
 *    guardaría solo una parte y dejaría al usuario sin saber qué quedó grabado.
 *    Quien persiste es el ciclo del documento, vía los refs registrados.
 *
 * 2. **Las pestañas ocultan, no desmontan.** Un `EntityContainer` desmontado
 *    pierde sus valores y desregistra su ref, así que capturar en la pestaña A,
 *    pasar a la B y guardar perdería A sin decir nada. Se pintan todas y se oculta
 *    con `hidden` — a diferencia de las pestañas de detalle del documento, que sí
 *    desmontan porque su grilla necesita medir el contenedor de scroll.
 *
 * ## El título lo pone SIEMPRE este componente
 *
 * `hideSubmitButton` viaja acompañado de `hideTitle` en todos los casos. El título
 * propio de `DynamicEntities` es un `h2 text-xl`, un peso tipográfico que en un
 * documento no se corresponde con nada: las secciones del encabezado se rotulan
 * con `text-sm font-bold`. Dejarlo salir metía un segundo nivel de titulación —y,
 * antes de este arreglo, una segunda tarjeta— dentro de la que ya aporta el bloque.
 * El nombre de cada entidad se rotula aquí, y solo cuando hay más de una: con una
 * sola, repetir "Datos adicionales" bajo "Campos adicionales" es ruido.
 */
export const DocumentDynamicEntities: React.FC<DocumentDynamicEntitiesProps> = ({
  grain,
  recordId,
  defaultTitleKey,
  readOnly,
  variant = 'card',
  className,
}) => {
  const { t } = useMasterDocumentTranslation();

  const inlineEntities = useMemo(
    () => grain.entities.filter((fe) => fe.renderMode === 'inline'),
    [grain.entities],
  );
  const tabEntities = useMemo(
    () => grain.entities.filter((fe) => fe.renderMode === 'tab'),
    [grain.entities],
  );

  const [activeTab, setActiveTab] = useState<string>(
    () => tabEntities[0]?.dynamicEntity?.code ?? '',
  );

  // Una entidad puede aparecer o desaparecer si el administrador la reconfigura
  // mientras el documento está abierto: sin esto la pestaña activa apuntaría a
  // una entidad que ya no existe y el cuerpo quedaría en blanco.
  const activeKey =
    tabEntities.some((fe) => fe.dynamicEntity?.code === activeTab)
      ? activeTab
      : (tabEntities[0]?.dynamicEntity?.code ?? '');

  const title =
    grain.labelKey && t(grain.labelKey) !== grain.labelKey
      ? t(grain.labelKey)
      : (grain.label ?? t(defaultTitleKey));

  // Con una sola entidad su nombre no aporta nada sobre el título del bloque.
  const showEntityNames = grain.entities.length > 1;

  // Un callback de ref ESTABLE por entidad. Una función inline se recrearía en
  // cada render y React desengancharía (`null`) y volvería a enganchar el ref en
  // cada uno: ruido puro, y una ventana en la que el registro queda vacío.
  //
  // El callback lee el grano por ref en vez de capturarlo: así es estable de
  // verdad y sigue apuntando al grano vigente si el Feature se recarga.
  const grainRef = useRef(grain);
  grainRef.current = grain;

  // Qué entidades tienen campos que pintar. Una entidad recién creada a la que todavía no le
  // pusieron campos devuelve una definición válida con `fields: []`, y sin esto el bloque dibujaba
  // una tarjeta con título y nada dentro — un estado alcanzable por cualquiera que cree la entidad
  // antes que sus campos.
  const [renderable, setRenderable] = useState<Record<string, boolean>>({});

  const renderableCallbacks = useRef(new Map<string, (v: boolean) => void>());
  const reportRenderable = useCallback((entityCode: string) => {
    let cb = renderableCallbacks.current.get(entityCode);
    if (!cb) {
      cb = (v: boolean) =>
        setRenderable((prev) => (prev[entityCode] === v ? prev : { ...prev, [entityCode]: v }));
      renderableCallbacks.current.set(entityCode, cb);
    }
    return cb;
  }, []);

  const registerCallbacks = useRef(new Map<string, (ref: DynamicEntitiesRef | null) => void>());
  const register = useCallback((entityCode: string) => {
    let cb = registerCallbacks.current.get(entityCode);
    if (!cb) {
      cb = (ref: DynamicEntitiesRef | null) => grainRef.current.registerRef(entityCode, ref);
      registerCallbacks.current.set(entityCode, cb);
    }
    return cb;
  }, []);

  // Se esconde solo cuando TODAS reportaron y ninguna tiene nada. Mientras falte alguna por
  // reportar el bloque se queda: decir "no hay nada" antes de saberlo lo haría parpadear.
  //
  // Y se esconde con `hidden`, sin desmontar: los formularios tienen que seguir montados para
  // poder reportar —desmontarlos los devolvería al estado «sin reportar» y el bloque volvería a
  // aparecer, en bucle— y para conservar sus refs. Mismo criterio que las pestañas.
  const allReported = grain.entities.every(
    (fe) => renderable[fe.dynamicEntity!.code] !== undefined,
  );
  const nothingToShow = allReported && !Object.values(renderable).some(Boolean);

  const entityName = (code: string, description?: string) => (
    <h4
      className={clsx(
        'mb-3 text-xs font-semibold uppercase tracking-wide',
        'text-content-secondary dark:text-dark-content-secondary',
      )}
    >
      {description || code}
    </h4>
  );

  const body = (
    <div className={clsx('space-y-6', variant === 'card' && 'px-4 py-4')}>
      {inlineEntities.map((fe) => (
        <div key={fe.dynamicEntity!.code}>
          {showEntityNames && entityName(fe.dynamicEntity!.code, fe.dynamicEntity!.description)}
          <EntityContainer
            ref={register(fe.dynamicEntity!.code)}
            featureCode={grain.featureCode}
            entityCode={fe.dynamicEntity!.code}
            recordId={recordId}
            hideSubmitButton
            hideTitle
            onRenderableChange={reportRenderable(fe.dynamicEntity!.code)}
          />
        </div>
      ))}

      {tabEntities.length > 0 && (
        <div>
          {/* Barra de pestañas siempre que haya entidades en ese modo, incluso una
              sola: es lo que el administrador pidió al elegir "Pestaña", y es el
              mismo criterio que sigue `MasterCrudDynamicEntities`. */}
          <Tabs
            items={tabEntities.map((fe) => ({
              id: fe.dynamicEntity!.code,
              label: fe.dynamicEntity!.description || fe.dynamicEntity!.code,
            }))}
            activeId={activeKey}
            onChange={setActiveTab}
            className="mb-4"
          />
          {tabEntities.map((fe) => (
            <div
              key={fe.dynamicEntity!.code}
              className={activeKey === fe.dynamicEntity!.code ? 'block' : 'hidden'}
            >
              <EntityContainer
                ref={register(fe.dynamicEntity!.code)}
                featureCode={grain.featureCode}
                entityCode={fe.dynamicEntity!.code}
                recordId={recordId}
                hideSubmitButton
                hideTitle
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (variant === 'bare') {
    return (
      <section
        className={clsx(
          nothingToShow && 'hidden',
          readOnly && 'pointer-events-none opacity-60',
          className,
        )}
        aria-label={title}
      >
        <h3
          className={clsx(
            'mb-3 text-sm font-bold',
            'text-content-primary dark:text-dark-content-primary',
          )}
        >
          {title}
        </h3>
        {body}
      </section>
    );
  }

  return (
    <section
      // Exactamente la tarjeta de `HeaderRenderer`: sombra suave en claro, borde en
      // oscuro. Copiar estos tokens y no inventar unos propios es lo que hace que el
      // bloque se lea como una sección más del documento y no como un injerto.
      className={clsx(
        // Sin tarjeta propia: ya está dentro de la del detalle (ADR-027 §E.7).
        'min-w-0',
        nothingToShow && 'hidden',
        readOnly && 'pointer-events-none opacity-60',
        className,
      )}
      aria-label={title}
    >
      {/* Mismo rótulo que una sección del encabezado: `text-sm font-bold`, px-4 py-3. */}
      <h3
        className={clsx(
          'px-4 py-3 text-sm font-bold',
          'text-content-primary dark:text-dark-content-primary',
        )}
      >
        {title}
      </h3>
      {body}
    </section>
  );
};
